"use client"

import { useState, useEffect } from "react"
import { createClient } from "@/lib/supabase/client"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { toast } from "sonner"
import { User, Loader2, Pencil, MapPin, Phone, Mail, Building2, ShieldCheck, Calendar } from "lucide-react"
import Image from "next/image"
import { useLanguage } from "@/lib/language-context"

interface ProfileFormProps {
    user: any
}

interface ProfileData {
    full_name: string
    company_name: string
    address: string
    city?: string
    state?: string
    zip_code?: string
    phone: string
}

export function ProfileForm({ user }: ProfileFormProps) {
    const { t } = useLanguage()
    const supabase = createClient()
    const [loading, setLoading] = useState(false)
    const [fetching, setFetching] = useState(true)
    const [isEditing, setIsEditing] = useState(false)
    const [profile, setProfile] = useState<ProfileData>({
        full_name: "",
        company_name: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        phone: "",
    })

    // Initialize with user metadata if available
    useEffect(() => {
        if (user?.user_metadata?.full_name) {
            setProfile(prev => ({ ...prev, full_name: user.user_metadata.full_name }))
        }
        fetchProfile()
    }, [user])

    const fetchProfile = async () => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .single()

            if (error && error.code !== 'PGRST116') {
                console.error('Error fetching profile:', error)
            }

            // Use user prop for initial metadata to avoid redundant fetch
            const metadata = user?.user_metadata || {}

            if (data || metadata) {
                setProfile({
                    full_name: data?.full_name || metadata?.full_name || "",
                    company_name: data?.company_name || "",
                    address: data?.address || "",
                    city: metadata?.city || "",
                    state: metadata?.state || "",
                    zip_code: metadata?.zip_code || "",
                    phone: data?.phone || "",
                })
                // Only switch to view mode if we have at least a name
                if (data?.full_name || metadata?.full_name) {
                    setIsEditing(false)
                }
            } else {
                setIsEditing(true)
            }
        } catch (error) {
            console.error('Error loading user data:', error)
        } finally {
            setFetching(false)
        }
    }

    const updateProfile = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)

        try {
            // 1. Update core profile data in 'profiles' table
            const { error: profileError } = await supabase.from('profiles').upsert({
                id: user.id,
                full_name: profile.full_name,
                company_name: profile.company_name,
                address: profile.address, // Keeping this as Street Address
                phone: profile.phone,
                updated_at: new Date().toISOString(),
            })

            if (profileError) throw profileError

            // 2. Update extra address details in user_metadata
            const { error: metadataError } = await supabase.auth.updateUser({
                data: {
                    city: profile.city,
                    state: profile.state,
                    zip_code: profile.zip_code,
                    full_name: profile.full_name,
                }
            })

            if (metadataError) throw metadataError

            toast.success("Profile updated successfully")
            setIsEditing(false)
        } catch (error: any) {
            console.error('Error updating profile:', error)
            toast.error("Error updating profile: " + (error.message || "Unknown error"))
        } finally {
            setLoading(false)
        }
    }

    if (fetching) {
        return (
            <Card className="md:col-span-1 h-fit">
                <CardContent className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </CardContent>
            </Card>
        )
    }

    // View Mode (Premium Bento Grid)
    if (!isEditing) {
        return (
            <div className="grid gap-6 md:col-span-1 h-fit">
                {/* Main Profile Header Card */}
                <Card className="relative overflow-hidden border-none shadow-xl bg-gradient-to-br from-white to-gray-50 group transition-all duration-300 hover:shadow-2xl">
                    <div className="absolute top-0 right-0 p-3 z-20">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 rounded-full bg-white/60 hover:bg-white text-gray-800 shadow-sm backdrop-blur-md transition-all border border-gray-100/50"
                            onClick={() => setIsEditing(true)}
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Decorative Background Element */}
                    <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary/5 blur-3xl group-hover:bg-primary/10 transition-colors duration-500"></div>
                    <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-accent/5 blur-3xl group-hover:bg-accent/10 transition-colors duration-500"></div>

                    <CardContent className="relative pt-10 pb-8 z-10">
                        <div className="flex flex-col items-center">
                            {/* Avatar with ring */}
                            <div className="relative p-1.5 rounded-full bg-gradient-to-tr from-primary to-accent shadow-lg mb-4">
                                <div className="h-28 w-28 rounded-full border-4 border-white bg-white overflow-hidden">
                                    <Image
                                        src="/default-avatar.png"
                                        alt="Profile"
                                        width={112}
                                        height={112}
                                        className="object-cover h-full w-full transform group-hover:scale-110 transition-transform duration-500"
                                    />
                                </div>
                                <div className="absolute bottom-1 right-1 h-6 w-6 rounded-full bg-green-500 border-2 border-white shadow-sm flex items-center justify-center">
                                    <ShieldCheck className="h-3 w-3 text-white" />
                                </div>
                            </div>

                            <h2 className="text-2xl font-black text-gray-900 tracking-tight">
                                {profile.full_name || t("account.active")}
                            </h2>

                            <div className="flex flex-col items-center gap-2 mt-2 w-full max-w-full overflow-hidden">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium border border-gray-200 max-w-full">
                                    <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{user.email}</span>
                                </span>

                                {profile.company_name && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20 max-w-full">
                                        <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                        <span className="truncate">{profile.company_name}</span>
                                    </span>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Info Bento Grid */}
                <div className="grid grid-cols-1 gap-4 w-full min-w-0">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full min-w-0">
                        {/* Phone Card */}
                        <Card className="border border-gray-100 shadow-sm bg-white/80 backdrop-blur-sm overflow-hidden group hover:border-primary/30 transition-all duration-300 min-w-0">
                            <CardHeader className="p-4 pb-1">
                                <CardTitle className="text-[10px] font-bold text-gray-400 uppercase tracking-normal sm:tracking-widest flex items-center gap-1.5 mb-1 group-hover:text-primary transition-colors truncate">
                                    <Phone className="h-3 w-3 flex-shrink-0" /> <span className="truncate">{t("account.phone")}</span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-4 pt-1">
                                <p className="text-sm font-semibold text-gray-800 truncate">{profile.phone || "---"}</p>
                            </CardContent>
                        </Card>

                        {/* Status Card */}
                        <Card className="border border-gray-100 shadow-sm bg-white/80 backdrop-blur-sm overflow-hidden group hover:border-primary/30 transition-all duration-300 min-w-0">
                            <CardHeader className="p-4 pb-1">
                                <CardTitle className="text-[10px] font-bold text-gray-400 uppercase tracking-normal sm:tracking-widest flex items-center gap-1.5 mb-1 group-hover:text-primary transition-colors truncate">
                                    <ShieldCheck className="h-3 w-3 flex-shrink-0" /> <span className="truncate">{t("account.member_status")}</span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-4 pt-1">
                                <div className="flex items-center gap-2 overflow-hidden">
                                    <span className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0"></span>
                                    <p className="text-sm font-bold text-green-600 truncate">{t("account.active")}</p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Address Card (Full width) */}
                    <Card className="border border-gray-100 shadow-sm bg-white/80 backdrop-blur-sm overflow-hidden group hover:border-primary/30 transition-all duration-300">
                        <CardHeader className="p-4 pb-1">
                            <CardTitle className="text-[10px] font-bold text-gray-400 uppercase tracking-normal sm:tracking-widest flex items-center gap-1.5 mb-1 group-hover:text-primary transition-colors">
                                <MapPin className="h-3 w-3 flex-shrink-0" /> {t("account.address")}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-1">
                            <p className="text-sm font-semibold text-gray-800 leading-relaxed">
                                {profile.address || "---"}
                            </p>
                            {(profile.city || profile.state || profile.zip_code) && (
                                <p className="text-xs text-gray-500 mt-1 font-medium bg-gray-50 px-2 py-1 rounded inline-block">
                                    {[profile.city, profile.state, profile.zip_code].filter(Boolean).join(", ")}
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Joined Date Card */}
                    <Card className="border border-gray-100 shadow-sm bg-white/50 backdrop-blur-sm border-dashed">
                        <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center">
                                    <Calendar className="h-4 w-4 text-gray-400" />
                                </div>
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t("account.joined")}</span>
                            </div>
                            <span className="text-sm font-bold text-gray-700">
                                {new Date(user.created_at || Date.now()).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                })}
                            </span>
                        </CardContent>
                    </Card>
                </div>
            </div>
        )
    }

    // Edit Mode (Sleek Form)
    return (
        <Card className="md:col-span-1 h-fit border-none shadow-2xl bg-white">
            <CardHeader className="bg-gray-50/50 border-b p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0 rotate-3">
                            <User className="h-6 w-6 text-primary -rotate-3" />
                        </div>
                        <div>
                            <CardTitle className="text-xl font-bold text-gray-900 tracking-tight">{t("account.edit_profile")}</CardTitle>
                            <CardDescription className="text-xs font-medium text-gray-500">{t("account.update_info")}</CardDescription>
                        </div>
                    </div>
                    {profile.full_name && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsEditing(false)}
                            className="text-xs font-bold hover:bg-gray-100"
                        >
                            {t("filters.clear_all")}
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="p-6">
                <form onSubmit={updateProfile} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="firstName" className="text-xs font-bold text-gray-600 uppercase tracking-wider">First Name</Label>
                            <Input
                                id="firstName"
                                value={profile.full_name.split(' ')[0] || ''}
                                onChange={(e) => {
                                    const lastName = profile.full_name.split(' ').slice(1).join(' ') || ''
                                    setProfile({ ...profile, full_name: `${e.target.value} ${lastName}`.trim() })
                                }}
                                placeholder="John"
                                className="h-11 border-gray-200 focus:border-primary focus:ring-primary/20 bg-gray-50/50"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="lastName" className="text-xs font-bold text-gray-600 uppercase tracking-wider">Last Name</Label>
                            <Input
                                id="lastName"
                                value={profile.full_name.split(' ').slice(1).join(' ') || ''}
                                onChange={(e) => {
                                    const firstName = profile.full_name.split(' ')[0] || ''
                                    setProfile({ ...profile, full_name: `${firstName} ${e.target.value}`.trim() })
                                }}
                                placeholder="Doe"
                                className="h-11 border-gray-200 focus:border-primary focus:ring-primary/20 bg-gray-50/50"
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="companyName" className="text-xs font-bold text-gray-600 uppercase tracking-wider">Company Name</Label>
                        <Input
                            id="companyName"
                            value={profile.company_name}
                            onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                            placeholder="Your Company Name"
                            className="h-11 border-gray-200 focus:border-primary focus:ring-primary/20 bg-gray-50/50"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="address" className="text-xs font-bold text-gray-600 uppercase tracking-wider">Street Address</Label>
                        <Input
                            id="address"
                            value={profile.address}
                            onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                            placeholder="123 Main St"
                            className="h-11 border-gray-200 focus:border-primary focus:ring-primary/20 bg-gray-50/50"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="city" className="text-xs font-bold text-gray-600 uppercase tracking-wider">City</Label>
                            <Input
                                id="city"
                                value={profile.city || ''}
                                onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                                placeholder="New York"
                                className="h-11 border-gray-200 focus:border-primary focus:ring-primary/20 bg-gray-50/50"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="state" className="text-xs font-bold text-gray-600 uppercase tracking-wider">State</Label>
                            <Input
                                id="state"
                                value={profile.state || ''}
                                onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                                placeholder="NY"
                                className="h-11 border-gray-200 focus:border-primary focus:ring-primary/20 bg-gray-50/50"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="zipCode" className="text-xs font-bold text-gray-600 uppercase tracking-wider">Zip Code</Label>
                            <Input
                                id="zipCode"
                                value={profile.zip_code || ''}
                                onChange={(e) => setProfile({ ...profile, zip_code: e.target.value })}
                                placeholder="10001"
                                className="h-11 border-gray-200 focus:border-primary focus:ring-primary/20 bg-gray-50/50"
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="phone" className="text-xs font-bold text-gray-600 uppercase tracking-wider">Phone Number</Label>
                        <div className="flex gap-2">
                            <Select
                                value={profile.phone.split(' ')[0] || "+91"}
                                onValueChange={(value) => {
                                    const number = profile.phone.split(' ').slice(1).join(' ')
                                    setProfile({ ...profile, phone: `${value} ${number}` })
                                }}
                            >
                                <SelectTrigger className="w-[100px] md:w-[110px] h-11 border-gray-200 bg-gray-50/50">
                                    <SelectValue placeholder="Code" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="+1">🇺🇸 +1</SelectItem>
                                    <SelectItem value="+44">🇬🇧 +44</SelectItem>
                                    <SelectItem value="+91">🇮🇳 +91</SelectItem>
                                    <SelectItem value="+49">🇩🇪 +49</SelectItem>
                                    <SelectItem value="+33">🇫🇷 +33</SelectItem>
                                    <SelectItem value="+81">🇯🇵 +81</SelectItem>
                                </SelectContent>
                            </Select>
                            <Input
                                id="phone"
                                value={profile.phone.split(' ').slice(1).join(' ')}
                                onChange={(e) => {
                                    const code = profile.phone.split(' ')[0] || "+91"
                                    setProfile({ ...profile, phone: `${code} ${e.target.value}` })
                                }}
                                placeholder="123 456 7890"
                                className="flex-1 h-11 border-gray-200 focus:border-primary focus:ring-primary/20 bg-gray-50/50"
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 pt-4">
                        <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90 h-12 text-sm font-bold shadow-lg shadow-primary/20 transition-all active:scale-[0.98]" disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {loading ? (t("featured.added") === "Added" ? "Saving..." : "सहेज रहा है...") : (t("products.add_to_cart") === "Add to Cart" ? "Save Changes" : "परिवर्तन सहेजें")}
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card >
    )
}
